import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import CounterDepositsSettings from '../deposits/CounterDepositsSettings';
import { DiscountSettings, DiscountSettingsSection, discountSectionId } from './DiscountPresetsField';
import { LoadFailed, SavedNote, SwitchRow, useSavedFlash } from './instantSettings';
import { patchSettings } from './patchSettings';
import { SettingsSheet, SheetBlock, SheetSection } from './sheet';
import { StoreLoadState } from './storeSettingsForm';

// Χωρίς διακόπτη από 12/10/2026: αριθμητικό μετρητών και «Αγαπημένα» πάντα ανοιχτά·
// auto_print_receipt / require_customer_info δεν τα διάβαζε κανείς. Ο server δεν τα στέλνει.
// Το print_ticket (δελτίο παραγγελίας) από 14/10/2026 στους «Εκτυπωτές» → «Τι πάει πού».
interface TakeAwaySettingsData extends DiscountSettings {
  id: number;
  store_id: number;
  auto_plastic_fee_enabled: boolean;
  delivery_note_enabled: boolean;
}

// Διάταξη «Φύλλο» (13/10/2026): Εκπτώσεις | Checkout | Προκαταβολές πελατών. Κάθε αλλαγή αποθηκεύεται αμέσως.
const DISCOUNTS = discountSectionId('takeaway');
const CHECKOUT = 'takeaway-checkout';
const DEPOSITS = 'takeaway-deposits';

const TakeAwaySettings: React.FC = () => {
  const { t } = useTranslation(['settings', 'deposits']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const [settings, setSettings] = useState<TakeAwaySettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedSection, flashSaved] = useSavedFlash();

  useEffect(() => {
    if (store?.id) {
      fetchSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/takeaway/${store?.id}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch takeaway settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async <K extends keyof TakeAwaySettingsData>(key: K, value: TakeAwaySettingsData[K]) => {
    if (!settings) return;
    const previous = settings[key];

    // Optimistic update — γυρίζει πίσω ΜΟΝΟ αυτό το πεδίο αν αποτύχει (4xx/5xx/δίκτυο).
    setSettings(s => (s ? { ...s, [key]: value } : s));

    const result = await patchSettings<TakeAwaySettingsData>(
      `${API_BASE_URL}/api/takeaway/${store?.id}/settings`, { [key]: value }
    );
    if (result.ok) {
      setSettings(result.data);
      flashSaved(String(key).startsWith('discount_') ? DISCOUNTS : CHECKOUT);
    } else {
      setSettings(s => (s ? { ...s, [key]: previous } : s));
      showToast('error', t(result.status === 403 ? 'settings:takeaway.adminOnly' : 'settings:takeaway.saveFailed'));
    }
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.operations')} title={t('settings:takeaway.title')} subtitle={t('settings:takeaway.subtitle')}>
      {loading ? (
        <SheetBlock><StoreLoadState status="loading" onRetry={fetchSettings} /></SheetBlock>
      ) : !settings ? (
        <SheetBlock><LoadFailed message={t('settings:takeaway.loadFailed')} onRetry={fetchSettings} /></SheetBlock>
      ) : (
        <>
          <DiscountSettingsSection
            idPrefix="takeaway"
            settings={settings}
            onChange={(key, value) => updateSetting(key, value as TakeAwaySettingsData[typeof key])}
            saved={savedSection === DISCOUNTS}
          />

          <SheetSection id={CHECKOUT} title={t('settings:takeaway.checkoutTitle')} hint={t('settings:takeaway.checkoutHint')}
            aside={<SavedNote show={savedSection === CHECKOUT} />}>
            {/* Το τέλος είναι υποχρεωτικό όπου σερβίρεται πλαστικό μιας χρήσης,
                γι' αυτό η περιγραφή λέει ρητά πότε επιτρέπεται να κλείσει και ότι
                το delivery δεν επηρεάζεται — αλλιώς κάποιος θα το γυρίσει «για να
                μη χρεώνει» και θα βρεθεί εκτεθειμένος. */}
            <SwitchRow
              title={t('settings:takeaway.autoPlasticFeeLabel')}
              subtitle={t('settings:takeaway.autoPlasticFeeDesc')}
              checked={settings.auto_plastic_fee_enabled !== false}
              onChange={(value) => updateSetting('auto_plastic_fee_enabled', value)}
            />
            <SwitchRow
              title={t('settings:takeaway.deliveryNoteLabel')}
              subtitle={t('settings:takeaway.deliveryNoteDesc')}
              checked={settings.delivery_note_enabled === true}
              onChange={(value) => updateSetting('delivery_note_enabled', value)}
            />
          </SheetSection>

          {/* Προκαταβολές πελατών (14/10/2026): ο ΙΔΙΟΣ διακόπτης του καταστήματος με τη Σάλα
              (PUT /stores/:id/deposits/settings) — εδώ για όσους δουλεύουν μόνο Take Away. Αποθηκεύεται μόνος
              του, όχι με τις ρυθμίσεις take away. */}
          {store?.id && (
            <SheetSection id={DEPOSITS} title={t('deposits:settings.takeawayTitle')} hint={t('deposits:settings.takeawayHint')}>
              <CounterDepositsSettings storeId={store.id} />
            </SheetSection>
          )}
        </>
      )}
    </SettingsSheet>
  );
};

export default TakeAwaySettings;
